import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConnection, useLocalPrefs, useMe, useServices } from '@/model';
import type { Message } from '@/model';
import { useNavigation } from '@/app/contract';
import { chatPath } from '@/app/paths';
import { Button, Toast } from '@/ui';
import { useDetails, useScope } from './resources/hooks';
import { compareVersions } from './domain';
import { LegalReviewSheet } from './legal/LegalReviewSheet';

export default function Runtime() {
  const { t } = useTranslation('me'); const services = useServices(); const nav = useNavigation(); const me = useMe(); const connection = useConnection(); const scope = useScope();
  const prefs = useLocalPrefs(); const details = useDetails(); const shown = useRef(new Set<string>()); const seen = useRef(new Set<string>());
  const [alert, setAlert] = useState<{ id: string; chat: string; title: string; text: string }>();
  useEffect(() => services.session.onRetire(event => {
    if (event.accountId !== scope.accountId) return;
    shown.current.clear(); seen.current.clear(); setAlert(undefined);
    if (event.reason === 'remove' || event.reason === 'deleted') {
      try { localStorage.removeItem('hylo.web.legal.seen.' + event.accountId); } catch { /* Storage may be unavailable. */ }
    }
  }), [services, scope]);
  useEffect(() => { setAlert(undefined); }, [me?.settings.push_previews, prefs.inTabAlerts, scope]);
  useEffect(() => {
    if (!me || !scope.valid() || prefs.inTabAlerts === false || connection.phase !== 'online') return;
    let alive = true;
    const blocked = services.people.blocked();
    const pending = new Map<string, { check(): void; stop(): void }>();
    const clear = () => { for (const entry of pending.values()) entry.stop(); setAlert(undefined); };
    const visible = () => { if (document.visibilityState !== 'visible') clear(); };
    // Core queries are lazy. Subscriptions keep these projections live until an arrival can be evaluated.
    const stopBlocked = blocked.subscribe(() => { setAlert(undefined); for (const entry of pending.values()) entry.check(); });
    const stopUpdates = services.sync.onUpdate(['message.new', 'message.edited', 'message.deleted', 'read.changed', 'block.changed', 'me.member_prefs'], update => {
      if (!alive || !scope.valid()) return;
      if (update.type !== 'message.new') { clear(); return; }
      if (document.visibilityState !== 'visible') return;
      const arrival = update.data.message as Message | undefined;
      if (!arrival || typeof arrival.id !== 'string' || typeof arrival.conversation_id !== 'string'
        || !Number.isSafeInteger(arrival.seq) || arrival.sender_id === me.account.id || arrival.deleted_at
        || seen.current.has(arrival.id) || pending.has(arrival.id)) return;
      if (window.location.pathname.includes('/chat/' + encodeURIComponent(arrival.conversation_id))) return;
      const canonical = services.messages.message?.(arrival.id);
      if (!canonical) return;
      const summaryLive = services.conversations.summary(arrival.conversation_id);
      const stops: Array<() => void> = []; let ready = false;
      const stop = () => { pending.delete(arrival.id); window.clearTimeout(expiry); for (const off of stops) off(); };
      const check = () => {
        if (!ready || !alive || !scope.valid() || !pending.has(arrival.id)) return;
        if (document.visibilityState !== 'visible' || services.prefs.prefs.get().inTabAlerts === false
          || services.sync.connection.get().phase !== 'online') { stop(); return; }
        const message = canonical.get(); const summary = summaryLive.get();
        if (!message || message.id !== arrival.id || !summary) return;
        const currentMe = services.me.me().get();
        if (!currentMe || currentMe.account.id !== scope.accountId) { stop(); return; }
        const mute = summary.conversation.me.mute_until;
        if (message.conversation_id !== arrival.conversation_id || message.deleted_at || message.sender_id === me.account.id
          || summary.left || !['all', 'mentions'].includes(summary.notify) || message.seq <= summary.conversation.me.read_seq
          || blocked.get().includes(message.sender_id) || typeof mute === 'string' && Date.parse(mute) > Date.now()
          || window.location.pathname.includes('/chat/' + encodeURIComponent(message.conversation_id))) { stop(); return; }
        if (summary.conversation.last_message?.id !== message.id) {
          if (summary.conversation.last_seq >= message.seq) stop();
          return;
        }
        if (summary.conversation.last_message.revision < message.revision) return;
        if (summary.notify === 'mentions' && !message.mentions?.includes(me.account.id) && message.reply_to?.sender_id !== me.account.id) { stop(); return; }
        seen.current.add(message.id); if (seen.current.size > 500) seen.current.delete(seen.current.values().next().value!);
        stop();
        const text = currentMe.settings.push_previews ? summary.lastMessage?.text ?? t('notifications.newMessage') : t('notifications.newMessage');
        setAlert({ id: message.id, chat: summary.id, title: summary.title, text });
      };
      // Bound pending work if a removed/unreadable projection never publishes.
      const expiry = window.setTimeout(stop, 30_000);
      if (pending.size >= 100) pending.values().next().value?.stop();
      pending.set(arrival.id, { check, stop });
      stops.push(canonical.subscribe(check), summaryLive.subscribe(check));
      ready = true; check();
    });
    const dispose = () => { alive = false; clear(); stopUpdates(); stopBlocked(); document.removeEventListener('visibilitychange', visible); };
    const stopRetire = services.session.onRetire(event => { if (event.accountId === scope.accountId) dispose(); });
    document.addEventListener('visibilitychange', visible);
    return () => { dispose(); stopRetire(); };
  }, [services, scope, me, connection.phase, prefs.inTabAlerts, t]);
  useEffect(() => {
    const legal = details.data?.legal;
    if (!legal || !me) return;
    if (legal.accepted_terms_version && compareVersions(legal.terms_version, legal.accepted_terms_version) <= 0
      && legal.accepted_privacy_version && compareVersions(legal.privacy_version, legal.accepted_privacy_version) <= 0) return;
    const version = legal.terms_version + '|' + legal.privacy_version;
    const key = 'hylo.web.legal.seen.' + me.account.id;
    try { if (localStorage.getItem(key) === version) return; } catch { /* Storage may be unavailable. */ }
    if (shown.current.has(version)) return;
    let close = () => {}; let cancelled = false;
    const tryPresent = () => {
      if (cancelled || !scope.valid() || document.visibilityState !== 'visible' || shown.current.has(version)
        || document.querySelector('[role="dialog"],[role="alertdialog"]')) return;
      shown.current.add(version); try { localStorage.setItem(key, version); } catch { /* In-memory seen set remains valid. */ }
      const handle = nav.present(<LegalReviewSheet legal={legal} onClose={() => close()} onShown={pair => shown.current.add(pair)} />, { size: 'large' }); close = handle.dismiss;
    };
    const timer = window.setInterval(tryPresent, 750);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [details.data?.legal, me, nav, scope]);
  return alert ? <div className="me-alert-host"><Toast open title={alert.title} onDismiss={() => setAlert(undefined)}
    action={<Button size="small" onClick={() => { nav.open(chatPath(nav.section, alert.chat)); setAlert(undefined); }}>{t('actions.open')}</Button>}>
    {alert.text}</Toast></div> : null;
}

